import {
  Habit,
  ID,
  RepeatingTask,
  ScheduleEntryState,
  Task,
  TaskActionType,
  TaskType,
  Timestamp,
  UpdateTask,
} from '@griply/types';

import {
  completeTaskForToday,
  CompleteTaskForTodayResultKind,
} from './complete-task-for-today';
import { computeHabitEntryUpdate } from './compute-habit-entry-update';

export enum TaskCompletionActionKind {
  UpdateTask = 'update-task',
  UpdateHabitSchedules = 'update-habit-schedules',
  UpdateGoalTaskCount = 'update-goal-task-count',
  OpenManualEntry = 'open-manual-entry',
  TrackTaskComplete = 'track-task-complete',
}

export type TaskCompletionAction =
  | {
      kind: TaskCompletionActionKind.UpdateTask;
      update: UpdateTask;
    }
  | {
      kind: TaskCompletionActionKind.UpdateHabitSchedules;
      updates: UpdateTask[];
    }
  | {
      kind: TaskCompletionActionKind.UpdateGoalTaskCount;
      goalId: ID;
      delta: 1 | -1;
    }
  | {
      kind: TaskCompletionActionKind.OpenManualEntry;
      habitId: ID;
      date: Timestamp;
    }
  | {
      kind: TaskCompletionActionKind.TrackTaskComplete;
      taskType: TaskType;
    };

export type ResolveTaskCompletionActionsOptions = {
  today: Timestamp;
  type?: ScheduleEntryState;
  value?: number;
  increment?: number;
  referenceDate?: Timestamp;
  toggleDayEntry?: boolean;
};

export const resolveTaskCompletionActions = (
  task: Task,
  options: ResolveTaskCompletionActionsOptions,
): TaskCompletionAction[] => {
  const {
    today,
    type = ScheduleEntryState.Complete,
    value,
    increment,
    referenceDate,
    toggleDayEntry,
  } = options;

  const result = completeTaskForToday(task, {
    today,
    type,
    referenceDate,
    value,
    increment,
    toggleDayEntry,
  });

  if (result.kind === CompleteTaskForTodayResultKind.ManualEntryRequired) {
    return [
      {
        kind: TaskCompletionActionKind.OpenManualEntry,
        habitId: task.id,
        date: referenceDate ?? today,
      },
    ];
  }

  if (result.kind === CompleteTaskForTodayResultKind.Ignored) {
    return [];
  }

  if (task.type === TaskType.Task) {
    const becomingComplete = !task.completedAt;
    const actions: TaskCompletionAction[] = [
      {
        kind: TaskCompletionActionKind.UpdateTask,
        update: result.update,
      },
    ];

    if (task.goalId && task.action?.type !== TaskActionType.GoalTimeslot) {
      actions.push({
        kind: TaskCompletionActionKind.UpdateGoalTaskCount,
        goalId: task.goalId,
        delta: becomingComplete ? 1 : -1,
      });
    }

    if (becomingComplete) {
      actions.push({
        kind: TaskCompletionActionKind.TrackTaskComplete,
        taskType: task.type,
      });
    }

    return actions;
  }

  const habitDate = referenceDate ?? new Date();
  const { newSchedules, shouldCompleteHabit } = computeHabitEntryUpdate(
    task as Habit | RepeatingTask,
    habitDate,
    { today, type, value, increment, toggle: toggleDayEntry },
  );

  const actions: TaskCompletionAction[] = [
    {
      kind: TaskCompletionActionKind.UpdateHabitSchedules,
      updates: [
        { id: task.id, schedules: newSchedules },
        ...(task.childIds?.map((id) => ({ id, completedAt: null })) ?? []),
      ],
    },
  ];

  if (shouldCompleteHabit) {
    actions.push({
      kind: TaskCompletionActionKind.TrackTaskComplete,
      taskType: task.type,
    });
  }

  return actions;
};
